import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  notification,
  Input,
  Select,
  Spin,
  Progress,
  Tooltip,
  Pagination,
} from "antd";
import { useEffect, useState, useCallback } from "react";
import { ReactSVG } from "react-svg";
import { useDropzone } from "react-dropzone";
import { useContextState } from "../../../context/ContextProvider";
import { useSelector } from "react-redux";
import {
  getAttachments,
  openAttachment,
  uploadAttachment,
  deleteAttachment,
  downloadTotalFiles,
  downloadSingleFile,
  getOrganizations,
  getQboOrganizations,
} from "../../../utils/organization";
import FilePreviewModal from "../../../components/FilePreviewModal";

import {
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import CustomModal from "../../../components/modal";


const FileUploader = ({ orgId, onBack, type = "xero" }) => {
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { breadCrumb } = useContextState();
  const userData = useSelector((state) => state.commonReducer.userData);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [overwrite, setOverwrite] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [organizationData, setOrganizationData] = useState(null);
  const [orgDataLoading, setOrgDataLoading] = useState(true);

  const [openingKey, setOpeningKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState({
    value: false,
    onConfirm: null,
  });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem("pageSize");
    return saved ? parseInt(saved, 10) : 10;
  });

  // Preview modal state
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFileData, setPreviewFileData] = useState({
    url: null,
    name: null,
    type: null,
  });
  const [userIpAddress, setUserIpAddress] = useState(null);


  const handlePageSizeChange = (page, size) => {
    setPageSize(size);
    setCurrentPage(1);
    localStorage.setItem("pageSize", size);
  };

  // Fetch user's IP address
  const fetchUserIpAddress = async () => {
    console.log("🌐 [FileUploader] Fetching user IP address...");
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      console.log("✅ [FileUploader] IP address fetched:", data.ip);
      setUserIpAddress(data.ip);
    } catch (error) {
      console.error("❌ [FileUploader] Failed to fetch IP address:", error);
      setUserIpAddress("Unknown");
    }
  };

  const fetchUploadedAttachments = async () => {
    try {
      setLoading(true);
      const token = await getIdTokenClaims();
      const response = await getAttachments(token.__raw, orgId);
      const normalizedFiles = (response.data.files || []).map((f) => ({
        ...f,
        size: Number(f.size),
        url: `${f.url}&token=${encodeURIComponent(token.__raw)}`,
      }));
      setUploadedFiles(normalizedFiles);
    } catch (error) {
      notification.error({ message: "Failed to load files" });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationData = async () => {
    try {
      setOrgDataLoading(true);
      const token = await getIdTokenClaims();
      const fetchApi = type === "qbo" ? getQboOrganizations : getOrganizations;
      const response = await fetchApi(token.__raw, "");
      console.log("Organizations response:", response.data);
      const organizations = response.data?.accounts || [];
      const orgData = organizations.find(org => org.id == orgId);
      console.log("Found organization:", orgData);
      console.log("Country code:", orgData?.country_code);
      setOrganizationData(orgData);
    } catch (error) {
      console.error("Failed to load organization data:", error);
    } finally {
      setOrgDataLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && orgId) {
      fetchUploadedAttachments();
      fetchOrganizationData();
      fetchUserIpAddress();
    }
  }, [isLoading, orgId]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const disallowedExtensions = ["exe", "bat", "sh", "cmd", "vbs", "py", "php", "js"];
      const filteredFiles = acceptedFiles.filter((file) => {
        const extension = file.name.split(".").pop().toLowerCase();
        if (disallowedExtensions.includes(extension)) {
          notification.warning({
            message: `File type .${extension} is not allowed`,
            description: `The file "${file.name}" was skipped due to security restrictions.`,
          });
          return false;
        }

        const isDuplicate = uploadedFiles.some(
          (f) => f.name.trim().toLowerCase() === file.name.trim().toLowerCase()
        );
        if (isDuplicate) {
          notification.warning({
            message: "Upload blocked: A file with the same name already exists.",
            description:
              "While Xero supports duplicate filenames, our system does not.",
          });
          return false;
        }
        return true;
      });

      const files = filteredFiles.map((file) => ({ file, progress: 0 }));
      setUploadQueue((prev) => [...prev, ...files]);
    },
    [uploadedFiles]
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const uploadNextFile = async () => {
    if (uploading || uploadQueue.length === 0) return;
    setUploading(true);
    const nextFile = uploadQueue[0];
    const formData = new FormData();
    formData.append("file", nextFile.file);
    formData.append("orgId", orgId);
    formData.append("overwrite", overwrite.toString());

    try {
      const token = await getIdTokenClaims();
      const response = await uploadAttachment(
        token.__raw,
        orgId,
        formData,
        (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          setProgress(percent);
        }
      );

      if (response.data?.skipped) {
        notification.warning({
          message: "Upload Skipped",
          description: response.data.message || "This file was not uploaded.",
        });
      } else {
        notification.success({
          message: "Upload Successful",
          description: `${nextFile.file.name} uploaded successfully.`,
        });
        fetchUploadedAttachments();
      }
    } catch (error) {
      notification.error({
        message: "Upload failed",
        description: error?.message || "Something went wrong during the upload.",
      });
    } finally {
      setUploadQueue((prev) => prev.slice(1));
      setProgress(0);
      setUploading(false);
    }
  };

  const openAttachmentHandler = async (key, fileName, fileExtension) => {
    console.log("🔵 [FileUploader] Opening attachment clicked", { key, fileName, fileExtension });
    setOpeningKey(key);
    const formData = new FormData();
    formData.append("url", key);

    try {
      console.log("🔵 [FileUploader] Fetching token...");
      const token = await getIdTokenClaims();

      console.log("🔵 [FileUploader] Calling openAttachment API", { orgId, key });
      const response = await openAttachment(token.__raw, orgId, formData);
      console.log("🔵 [FileUploader] API Response received:", response.data);

      if (response.data.privateUrl) {
        console.log("✅ [FileUploader] Private URL received:", response.data.privateUrl);

        const previewData = {
          url: response.data.privateUrl,
          name: fileName,
          type: fileExtension,
        };

        console.log("🔵 [FileUploader] Setting preview file data:", previewData);
        setPreviewFileData(previewData);

        console.log("🔵 [FileUploader] Opening preview modal...");
        setPreviewModalVisible(true);

        console.log("✅ [FileUploader] Modal should now be visible");
      } else {
        console.error("❌ [FileUploader] No privateUrl in response", response.data);
        notification.error({ message: response.data.message || "No URL received." });
      }
    } catch (error) {
      console.error("❌ [FileUploader] Error opening attachment:", error);
      notification.error({ message: "Could not open file." });
    } finally {
      setOpeningKey(null);
      console.log("🔵 [FileUploader] openAttachmentHandler completed");
    }
  };

  const downloadAttachmentHandler = async(url) => {
    const onConfirmDownload = async () => {
      try {
        setIsButtonLoading(true);
        setOpeningKey(url);

        const token = await getIdTokenClaims();
        const response = await downloadSingleFile(token.__raw, url);

      if (response.status == 200) {
        notification.success({
          message: "You will receive an email once your download is complete with a link to download the files",
          duration: 5,
        });
      } else {
        notification.error({ message: response.data.message || "No URL received." });
      }
      } catch (e) {
        console.error(e);
        notification.error({ message: "Failed to download file." });
      } finally {
        setIsButtonLoading(false);
        setOpeningKey(null);
        onCloseModal();
      }
    };

    setModalOpen({
      value: true,
      onConfirm: onConfirmDownload,
    });
  }

 const downloadWholeBackup = async () => {
    const onConfirmDownload = async () => {
      try {
        setIsButtonLoading(true);
        const token = await getIdTokenClaims();
        setDownloadLoading(true);

        downloadTotalFiles(token.__raw, orgId)
                  .then((res) => {
                    notification.success({
                      message: "You will receive an email once your download is complete with a link to download the files",
                      duration: 5,
                    });
                  })
                  .catch((err) => {
                    console.error(err);
                  })
                  .finally(() => {
                    setDownloadLoading(false);
                    onCloseModal();
                    return true;
                  });
        // downloadTotalFiles(token.__raw,orgId);

      } catch (e) {
        onCloseModal();
      }
    };

    setModalOpen({
      value: true,
      onConfirm: onConfirmDownload,
    });
  };
  
 
  const deleteAttachmentHandler = async (key) => {
    setDeletingKey(key);
    const formData = new FormData();
    formData.append("url", key);
    try {
      const token = await getIdTokenClaims();
      const response = await deleteAttachment(token.__raw, orgId, formData);
      if (response.data.success) {
        notification.success({
          message: response.data.message || "Successfully removed attachment.",
        });
        setUploadedFiles((prev) => prev.filter((f) => f.key !== key));
      } else {
        notification.error({
          message: response.data.message || "Could not delete attachment.",
        });
      }
    } catch {
      notification.error({ message: "Could not delete file." });
    } finally {
      setDeletingKey(null);
    }
  };

  const onCloseModal = () => {
    setModalOpen({
      value: false,
      onConfirm: null,
    });
    setIsButtonLoading(false);
  };

  useEffect(() => {
    if (!uploading && uploadQueue.length > 0) {
      uploadNextFile();
    }
  }, [uploadQueue, uploading]);

  const getFileIcon = (ext = "") => {
    const map = {
      pdf: "📄",
      doc: "📝",
      docx: "📝",
      xls: "📊",
      xlsx: "📊",
      jpg: "🖼️",
      jpeg: "🖼️",
      png: "🖼️",
      zip: "🗜️",
      rar: "🗜️",
    };
    return map[ext.toLowerCase()] || "📁";
  };

  const filteredFiles = uploadedFiles
    .filter((file) => file.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case "size":
          return (a.size || 0) - (b.size || 0);
        case "extension":
          return (a.extension || "").localeCompare(b.extension || "");
        default:
          return (a.name || "").localeCompare(b.name || "");
      }
    });

  const paginatedFiles = filteredFiles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => onBack()}
            className="flex items-center"
            type="default"
          >
            <ReactSVG src="/assets/icons/arrow-left.svg" />
            <span className="ml-2">Back</span>
          </Button>
          <div className="flex items-center gap-2 text-sm text-gray-600 ml-4">
            {breadCrumb.map((item, index) => (
              <span key={index} className={index === breadCrumb.length - 1 ? "font-semibold" : ""}>
                {item.label}
                {index < breadCrumb.length - 1 && <span className="mx-1">&gt;</span>}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 mb-4">
        <Input
          placeholder="Search files..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="sm:w-1/3"
        />
        <Select
          className="sm:w-1/4"
          value={sortBy}
          onChange={setSortBy}
          options={[
            { label: "Sort by Name", value: "name" },
            { label: "Sort by Size", value: "size" },
            { label: "Sort by Extension", value: "extension" },
          ]}
        />
      </div>

      {/* Dropzone - Only for AU organizations */}
      {(orgDataLoading || organizationData?.country_code === 'AU') && (
        <>
          <div
            {...getRootProps()}
            className="w-full border border-dashed border-gray-400 p-6 rounded-lg text-center text-gray-600 bg-gray-50 hover:bg-gray-100 cursor-pointer transition"
          >
            <input {...getInputProps()} />
            Drag & drop files here, or click to select
          </div>

          {/* Upload Progress */}
          {uploadQueue.length > 0 && (
            <div className="mt-4">
              <h3 className="font-medium mb-2">Uploading: {uploadQueue[0].file.name}</h3>
              <Progress percent={progress} status="active" />
            </div>
          )}
        </>
      )}

      {/* Uploaded Files */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-2">Uploaded Files</h2>
        {downloadLoading ? (
          <Spin />
        ) : (
          uploadedFiles.length > 0 && (
            <Button type="primary" onClick={downloadWholeBackup}>
              Download as ZIP
            </Button>
          )
        )}
              
        {loading ? (
          <Spin />
        ) : (
          <>
            <ul className="divide-y divide-gray-200">
              {paginatedFiles.map((file, idx) => (
                <li key={file.key || idx} className="flex justify-between items-center py-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{getFileIcon(file.extension)}</span>
                    <div>
                      <div className="font-medium text-gray-800">{file.name}</div>
                      <div className="text-sm text-gray-500">
                        {file.extension?.toUpperCase()} · {(file.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tooltip title="Download">
                      <Button
                        icon={<DownloadOutlined />}
						onClick={() => downloadAttachmentHandler(file.url)}
						loading={openingKey === file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-blue-600"
                        type="text"
                      />
                    </Tooltip>
                    <Tooltip title="Preview File">
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => openAttachmentHandler(file.key, file.name, file.extension)}
                        loading={openingKey === file.key}
                        className="text-gray-600 hover:text-blue-600"
                        type="text"
                      />
                    </Tooltip>
					{/* <Tooltip title="Delete Attachment">
                      <Button
                        icon={<DeleteOutlined />}
                        onClick={() => deleteAttachmentHandler(file.key)}
                        loading={deletingKey === file.key}
                        className="text-red-600 hover:text-red-800"
                        type="text"
                      />
                    </Tooltip> */}
                  </div>
                </li>
              ))}
            </ul>
            {filteredFiles.length > 0 && (
              <div className="mt-4 flex justify-end">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredFiles.length}
                  onChange={setCurrentPage}
                  showSizeChanger
                  onShowSizeChange={handlePageSizeChange}
                  pageSizeOptions={["5", "10", "25", "50","100"]}
                />
              </div>
            )}
          </>
        )}
      </div>

      <CustomModal
        modalOpen={modalOpen.value}
        onClose={onCloseModal}
        title={
          <div className="text-white text-lg font-bold bg-black px-4 py-2">
            Terms of Service
          </div>
        }
        footer={[
          <Button
            key="disagree"
            onClick={onCloseModal}
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-md px-4 py-2"
          >
            I Disagree
          </Button>,
          <Button
            key="agree"
            type="primary"
            onClick={modalOpen.onConfirm}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-md px-4 py-2"
            loading={isButtonLoading}
          >
            I Agree
          </Button>,
        ]}
      >
        <div className="text-gray-700 text-sm leading-6 max-h-72 overflow-y-auto">
          <div className="px-6 py-4">
						<b>Secure Download Request</b>

						<p>
							Once you confirm, an email will be sent to all users linked to this organization.

							<br/><br/>

							<strong>30-Minute Cancel Window:</strong><br/>
							Every org member will receive an email with a Cancel link. If anyone cancels within 30 minutes, the download will be stopped.

							<br/><br/>

							<strong>If No One Cancels:</strong><br/>

							After 30 minutes, you'll receive another email with a secure download link and password. You'll need to enter the password to open the file.

							<br/><br/>

							<strong>Important Notes:</strong><br/>

							<ul>
								<li>Any user in your organization can cancel within 30 minutes.</li>
								<li>Download link expires after 24 hours.</li>
								<li>After downloading, you'll get a final email with the extraction password.</li>
							</ul>
						</p>
          </div>
        </div>
      </CustomModal>

      {/* File Preview Modal */}
      {console.log("🟢 [FileUploader] Rendering FilePreviewModal", {
        previewModalVisible,
        previewFileData,
        watermarkData: {
          userName: userData?.email || userData?.name,
          organizationName: organizationData?.xero_organization_name || organizationData?.name,
          timestamp: new Date().toLocaleString(),
          ipAddress: userIpAddress,
        }
      })}
      <FilePreviewModal
        visible={previewModalVisible}
        onClose={() => {
          console.log("🔵 [FileUploader] Closing preview modal");
          setPreviewModalVisible(false);
          setPreviewFileData({ url: null, name: null, type: null });
        }}
        fileUrl={previewFileData.url}
        fileName={previewFileData.name}
        fileType={previewFileData.type}
        orgId={orgId}
        watermarkData={{
          userName: userData?.email || userData?.name || "Unknown User",
          organizationName: organizationData?.xero_organization_name || organizationData?.name || "Unknown Organization",
          timestamp: new Date().toLocaleString(),
          ipAddress: userIpAddress || "Unknown",
        }}
      />
    </div>
  );
};

export default FileUploader;
